import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { logout } from '@/shared/store/authSlice';
import { hasAdminRole, hasStaffRole } from '@/shared/utils/authUtils';
import { useNebiTheme } from '@/shared/theme/NebiThemeProvider';
import nebiLogoInk from '@/assets/images/nebi-logo.png';
import nebiLogoCream from '@/assets/images/nebi-branco.png';
import styles from './SiteHeader.module.scss';

/** Breakpoint do design novo (960px) — diferente da escala `$bp-*` antiga. */
const DESK_QUERY = '(min-width: 960px)';

const LINKS = [
  { to: '/home', label: 'Início' },
  { to: '/cursos', label: 'Cursos' },
  { to: '/conhecanos', label: 'Sobre' },
];

const LOGGED_LINKS = [{ to: '/dashboard-aluno', label: 'Minha aprendizagem' }];

/** Iniciais para o avatar: primeira letra do primeiro e do último nome. */
const initialsOf = (name?: string | null): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

/** Círculo meio preenchido do toggle de tema; estático, fora do render. */
const THEME_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
    <path d="M12 3.5a8.5 8.5 0 0 1 0 17Z" fill="currentColor" />
  </svg>
);

export const SiteHeader = () => {
  const { isAuthenticated, token, user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useNebiTheme();
  const isDesk = useMediaQuery(DESK_QUERY);

  const [menuOpen, setMenuOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const userBoxRef = useRef<HTMLDivElement>(null);
  // Gatilhos guardados para devolver o foco ao fechar pelo Escape.
  const avatarRef = useRef<HTMLButtonElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);

  // Qualquer navegação fecha o que estiver aberto.
  useEffect(() => {
    setMenuOpen(false);
    setUserOpen(false);
  }, [location.pathname]);

  // O menu mobile só existe abaixo de 960px; ao passar para desktop, some.
  useEffect(() => {
    if (isDesk) setMenuOpen(false);
  }, [isDesk]);

  // Clique fora e Escape fecham o dropdown da conta.
  useEffect(() => {
    if (!userOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!userBoxRef.current?.contains(e.target as Node)) setUserOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setUserOpen(false);
      avatarRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userOpen]);

  // Mesmo tratamento do dropdown: Escape fecha e o foco volta ao hambúrguer.
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      burgerRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const handleLogout = () => {
    setUserOpen(false);
    setMenuOpen(false);
    dispatch(logout());
    navigate('/login');
  };

  const isStaff = hasStaffRole(token);
  const staffLabel = hasAdminRole(token) ? 'Área Admin' : 'Área do Professor';
  const links = isAuthenticated ? [...LINKS, ...LOGGED_LINKS] : LINKS;

  const themeLabel =
    theme === 'noturno' ? 'Mudar para tema claro (Pergaminho)' : 'Mudar para tema escuro (Noturno)';

  return (
    <header className={styles.header}>
      <div className={styles.bar}>
        <Link to="/home" className={styles.brand} aria-label="NEBI — início">
          {/* Um arquivo por tema; o CSS escolhe via `--nebi-logo-ink` / `--nebi-logo-cream`. */}
          <img src={nebiLogoInk} alt="NEBI" className={styles.logoInk} />
          <img src={nebiLogoCream} alt="" className={styles.logoCream} />
          {isDesk && (
            <span className={styles.strapline}>
              Núcleo de Ensino
              <br />
              Bíblico
            </span>
          )}
        </Link>

        {isDesk && (
          <nav aria-label="Principal" className={styles.nav}>
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) => clsx(styles.navLink, isActive && styles.navLinkActive)}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className={styles.actions} ref={userBoxRef}>
          <button
            type="button"
            onClick={toggleTheme}
            className={styles.themeToggle}
            aria-label={themeLabel}
            title={themeLabel}
          >
            {THEME_ICON}
          </button>

          {!isAuthenticated && isDesk && (
            <Link to="/login" className={styles.enter}>
              Entrar
            </Link>
          )}

          {isAuthenticated && (
            <button
              ref={avatarRef}
              type="button"
              onClick={() => {
                setUserOpen((v) => !v);
                setMenuOpen(false);
              }}
              className={styles.avatar}
              aria-label="Menu da conta"
              aria-expanded={userOpen}
              aria-haspopup="menu"
            >
              {initialsOf(user?.name)}
            </button>
          )}

          {!isDesk && (
            <button
              ref={burgerRef}
              type="button"
              onClick={() => {
                setMenuOpen((v) => !v);
                setUserOpen(false);
              }}
              className={styles.burger}
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          )}

          {userOpen && (
            <div role="menu" className={styles.dropdown}>
              <div className={styles.dropdownHead}>
                <div className={styles.dropdownName}>{user?.name ?? 'Minha conta'}</div>
                <div className={styles.dropdownRole}>{isStaff ? staffLabel : 'Aluno(a)'}</div>
              </div>
              <Link to="/dashboard-aluno" role="menuitem" className={styles.dropdownItem}>
                Minha aprendizagem
              </Link>
              <Link to="/minhas-compras" role="menuitem" className={styles.dropdownItem}>
                Minhas compras
              </Link>
              {isStaff && (
                <Link
                  to="/admin"
                  role="menuitem"
                  className={styles.dropdownItem}
                  title={`Acesso à ${staffLabel}`}
                >
                  {staffLabel}
                </Link>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className={clsx(styles.dropdownItem, styles.dropdownLogout)}
              >
                Sair
              </button>
            </div>
          )}
        </div>
      </div>

      {!isDesk && menuOpen && (
        <div className={styles.mobileMenu}>
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                clsx(styles.mobileLink, isActive && styles.mobileLinkActive)
              }
            >
              {l.label}
            </NavLink>
          ))}
          {isAuthenticated && (
            <NavLink to="/minhas-compras" className={styles.mobileLink}>
              Minhas compras
            </NavLink>
          )}
          {isAuthenticated && isStaff && (
            <NavLink to="/admin" className={styles.mobileLink} title={`Acesso à ${staffLabel}`}>
              {staffLabel}
            </NavLink>
          )}
          {isAuthenticated ? (
            <button type="button" onClick={handleLogout} className={styles.mobileCta}>
              Sair
            </button>
          ) : (
            <Link to="/login" className={styles.mobileCta}>
              Entrar
            </Link>
          )}
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
